import { useNavigate, useParams } from 'react-router-dom';
import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Users, 
  ArrowLeft, 
  Loader, 
  AlertCircle 
} from 'lucide-react'
import { apiClient } from '@/lib/api'
import TeamDetailTabs from '@/components/teams/TeamDetailTabs'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'



interface TeamDetails {
  id: number
  uuid: string
  name: string
  description: string
  team_type: string
  max_members: number
  member_count: number
  is_active: boolean
  created_at: string
  team_lead: {
    id: number
    name: string
    email: string
  } | null
}

export default function TeamDetailsPage() {
  const navigate = useNavigate()
  const params = useParams()
  const teamId = params.id as string
  const teamsEnabled = useFeatureFlag('recruiterTeams')

  const [team, setTeam] = useState<TeamDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!teamsEnabled) {
      return
    }
    if (teamId) {
      loadTeamData()
    }
  }, [teamId, teamsEnabled])

  const loadTeamData = async () => {
    if (!teamsEnabled) return

    try {
      setLoading(true)
      setError(null)
      
      const response = await apiClient.getTeamDetails(teamId)
      if (response.success && response.data?.team) {
        const teamData = response.data.team
        setTeam(teamData)
      } else {
        setError('Team not found or access denied')
      }
    } catch (error) {
      console.error('Failed to load team data:', error)
      setError('Failed to load team data')
    } finally {
      setLoading(false)
    }
  }



  const getTeamTypeColor = (type: string) => {
    switch (type) {
      case 'recruiting':
        return 'bg-blue-100 text-blue-800'
      case 'hiring':
        return 'bg-green-100 text-green-800'
      case 'hr':
        return 'bg-purple-100 text-purple-800'
      case 'operations':
        return 'bg-orange-100 text-orange-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  if (!teamsEnabled) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-8rem)] text-center space-y-4">
        <AlertCircle className="w-10 h-10 text-gray-400" />
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Teams coming soon</h1>
          <p className="text-gray-600 mt-2 max-w-lg">
            This teams module is disabled in the current environment while we finalize it.
          </p>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading team details...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Error Loading Team</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => navigate('/recruiter/teams')}
            className="text-blue-600 hover:text-blue-800"
          >
            ← Back to Teams
          </button>
        </div>
      </div>
    )
  }

  if (!team) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Users className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Team Not Found</h2>
          <p className="text-gray-600 mb-4">The team you're looking for doesn't exist or you don't have access to it.</p>
          <button 
            onClick={() => navigate('/recruiter/teams')}
            className="text-blue-600 hover:text-blue-800"
          >
            ← Back to Teams
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/recruiter/teams')}
                className="text-gray-500 hover:text-gray-700 p-2 rounded-md hover:bg-gray-100"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{team.name}</h1>
                <p className="text-sm text-gray-500">{team.description || 'No description provided'}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${getTeamTypeColor(team.team_type)}`}>
                {team.team_type}
              </span>
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                team.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {team.is_active ? 'Active' : 'Inactive'}
              </span>
              <button
                onClick={() => navigate(`/recruiter/teams/${teamId}/manage`)}
                className="px-3 py-1 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700"
              >
                Manage
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <TeamDetailTabs teamId={teamId} team={team} />
      </div>
    </div>
  )
}
